// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FeaturedProductsCard from "@guestsupply/App/Components/Header/FeaturedProductsCard";
import Notify from "@guestsupply/App/Components/Notify";
import loadFeaturedProducts from "@guestsupply/App/Handlers/XNG/FeaturedProducts";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface FeaturedProductsProps {
    productNumbers: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    featuredProductsObject: state.xFeaturedProducts.featuredProducts,
    isLoading: state.xFeaturedProducts.isLoading,
    headerHeight: state.xHeader?.headerHeight,
});

const mapDispatchToProps = {
    loadFeaturedProducts,
    updateProductInfo,
};

type Props = FeaturedProductsProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedProductsStyle {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    featuredProductsTypography?: TypographyProps;
    featuredProductsItemsWrapper?: InjectableCss;
    notify?: TypographyProps;
}

export const featuredProductsStyles: FeaturedProductsStyle = {};

const styles = featuredProductsStyles;

const StyledSection = getStyledWrapper("section");
const FeaturedProducts: FC<Props> = ({
    productNumbers,
    loadFeaturedProducts,
    featuredProductsObject,
    isLoading,
    headerHeight,
}) => {
    const featuredProducts: ProductModel[] = featuredProductsObject[productNumbers];
    useEffect(() => {
        if (productNumbers.length) {
            loadFeaturedProducts({ productNumbers });
        }
    }, []);

    // XNG-CHANGES:GUEST-2653
    if (!featuredProducts?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...{ headerHeight }}>
            <Typography {...styles.featuredProductsTypography}>{translate("Featured Products")}</Typography>
            {isLoading && (
                <StyledSection {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledSection>
            )}
            <StyledWrapper {...styles.featuredProductsItemsWrapper}>
                {featuredProducts?.length > 0 &&
                    featuredProducts
                        .slice(0, 5)
                        .map((product, index) => <FeaturedProductsCard product={product} key={index} />)}
            </StyledWrapper>
            {!featuredProducts?.length && !isLoading && (
                <Notify
                    {...styles.notify}
                    type="info"
                    data-test-selector="cartlineHeader_warningSomeCartLinesNotPriced"
                >
                    {siteMessage("Custom_Header_NoFeaturedProducts", "No Featured Products")}
                </Notify>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProducts);
